import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { isEqual } from 'lodash-es';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useMapped$, useState$ } from '@/js/libs/observable';
import { useSearchParams } from '@/js/libs/router';
import { TypedObject } from '@/js/libs/utils/typed-object';

import { StorageKey } from 'src/hooks/useSessionStorage.ts';
import { MultiFilter as MultiFilterComponent, ClearFiltersButton as ClearFiltersButtonComponent } from './components/index.ts';
import { ADD_TO_ASYNC_FILTER_CONFIGS_SYMBOL, EMPTY_STORAGE_HOOK_RESULT } from './constants.ts';
import { isAsyncFilterConfig, isSyncFilterConfig } from './typeGuards.ts';
import type {
  AsyncFilterConfigs,
  FilterObject,
  FilterValue,
  GetFilterValueReturn,
  IsFilterHiddenFn,
  PersistedFilterObject,
  PersistedFilterValue,
  UpdateFilterFn,
  UseFiltersConfig,
  UseFiltersReturn,
} from './types.ts';
import { useFiltersSessionStorage } from './useFiltersSessionStorage.ts';

function queryParamsToObject<TFilters extends FilterObject>(
  searchParams: URLSearchParams,
  keys: Array<keyof TFilters>,
): Record<keyof TFilters, string | null> {
  return Object.fromEntries(keys.map((key) => [key, searchParams.get(String(key))])) as Record<keyof TFilters, string | null>;
}

export const useFilters = <TFilters extends FilterObject>({
  filterConfigs,
  storageScope,
  storageKey = StorageKey.FILTERS,
}: UseFiltersConfig<TFilters>): UseFiltersReturn<TFilters> => {
  const [searchParams, setSearchParams] = useSearchParams();

  const hasPersistence = Boolean(storageScope);

  const filterKeys = useMemo(() => TypedObject.keys(filterConfigs), [filterConfigs]);
  // Helps to avoid infinite re-renders when hidden filters are changed
  const hiddenFilterKeysRef = useRef<Array<keyof TFilters>>(filterKeys.filter((key) => filterConfigs[key].hidden));
  // Helps to check if the query params have changed externally
  const searchParamsRef = useRef<Record<keyof TFilters, string | null>>(queryParamsToObject(searchParams, filterKeys));

  const storageHookResult = useFiltersSessionStorage<TFilters>(storageKey, storageScope);

  const { loadStorageFilterField, saveStorageFilterField, removeStorageFilterField } = hasPersistence
    ? storageHookResult
    : EMPTY_STORAGE_HOOK_RESULT;

  // Cache filter state: search params override storage when any param exists
  const { current: areSearchParamFiltersEmpty } = useRef(filterKeys.every((key) => searchParams.get(String(key)) === null));
  // Check only local scope filters, external scope filters are considered as default values
  const { current: areStorageParamFiltersEmpty } = useRef(filterKeys.every((key) => loadStorageFilterField(String(key)) === null));

  const [areFiltersPreloading$, setAreFiltersPreloading] = useState$(false);
  const asyncFilterConfigsRef = useRef<AsyncFilterConfigs<TFilters>>([]);

  // Helper function to determine stored value source
  const getStoredValue = useCallbackRef((key: keyof TFilters, disabledStoragePersist?: boolean): PersistedFilterValue => {
    if (!hasPersistence) return null;

    // If ANY search param exists, ignore storage entirely
    if (!areSearchParamFiltersEmpty) {
      return searchParams.get(String(key));
    }

    // Only use storage if search params are completely empty
    if (!areStorageParamFiltersEmpty && !disabledStoragePersist) {
      return loadStorageFilterField(String(key), filterConfigs[key].customScope);
    }

    return null;
  });

  const getPersistedValues = useCallbackRef((): PersistedFilterObject<TFilters> => {
    const persistedValues = {} as PersistedFilterObject<TFilters>;

    for (const key of filterKeys) {
      persistedValues[key] = getStoredValue(key);
    }

    return persistedValues;
  });

  const getFilterValue = useCallbackRef((key: keyof TFilters): GetFilterValueReturn => {
    const config = filterConfigs[key];
    const { defaultValue, emptyValue, disabledStoragePersist } = config;

    if (!hasPersistence || (areSearchParamFiltersEmpty && areStorageParamFiltersEmpty && !config.customScope)) {
      // Priority: defaultValue, then (only when no defaultValue) defaultStoredValue via asyncDeserialize.
      // Hidden filters never receive a default so it isn't persisted to the URL behind a non-visible filter.
      if (
        !config.hidden &&
        defaultValue == null &&
        hasPersistence &&
        isAsyncFilterConfig(config) &&
        config.asyncDeserialize &&
        !!config.defaultStoredValue
      ) {
        return { symbol: ADD_TO_ASYNC_FILTER_CONFIGS_SYMBOL, storedValue: config.defaultStoredValue };
      }
      return defaultValue;
    }

    // Consider customScope value as a default value
    const isScopedDefaultValue = areSearchParamFiltersEmpty && areStorageParamFiltersEmpty && config.customScope;
    const defaultEmptyValue = isScopedDefaultValue ? defaultValue : emptyValue;

    try {
      const storedValue = isScopedDefaultValue
        ? loadStorageFilterField(key, config.customScope)
        : getStoredValue(key, disabledStoragePersist);

      if (isAsyncFilterConfig(config)) {
        // Return symbol + value for async processing, or empty if no stored data
        return storedValue !== null && config.asyncDeserialize
          ? { symbol: ADD_TO_ASYNC_FILTER_CONFIGS_SYMBOL, storedValue }
          : defaultEmptyValue;
      }

      // Sync filter - pass all persisted values to syncDeserialize
      if (storedValue !== null && isSyncFilterConfig(config) && config.syncDeserialize) {
        const persistedValues = getPersistedValues();
        return config.syncDeserialize(storedValue, persistedValues) ?? defaultEmptyValue;
      }
      return defaultEmptyValue;
    } catch {
      return defaultEmptyValue;
    }
  });

  const computeDefaultFilters = useCallbackRef((): TFilters => {
    const defaults = {} as TFilters;
    const asyncFilterConfigs: AsyncFilterConfigs<TFilters> = [];

    for (const key of filterKeys) {
      const config = filterConfigs[key];
      const value = getFilterValue(key);

      if (isSyncFilterConfig(config)) {
        defaults[key] = value as TFilters[keyof TFilters];
      } else if (isAsyncFilterConfig(config)) {
        if (typeof value === 'object' && value !== null && 'symbol' in value && value.symbol === ADD_TO_ASYNC_FILTER_CONFIGS_SYMBOL) {
          // Collect async filters that need API calls for deserialization
          asyncFilterConfigs.push({ key, config, storedValue: value.storedValue });
          // Set to emptyValue initially, will be updated after async deserialization
          defaults[key] = config.emptyValue as TFilters[keyof TFilters];
        } else {
          // No stored value, use the returned value (should be emptyValue)
          defaults[key] = value as TFilters[keyof TFilters];
        }
      }
    }

    asyncFilterConfigsRef.current = asyncFilterConfigs;

    return defaults;
  });

  // Separate function to compute initial multiFilter
  const computeInitialMultiFilter = useCallbackRef(
    (filters: TFilters): Array<keyof TFilters> =>
      filterKeys.filter((key) => {
        const { isMainFilter, emptyValue, compare, isSelectedByDefault } = filterConfigs[key];
        if (isMainFilter) return false;
        const currentValue = filters[key];
        return currentValue !== undefined && (!compare(currentValue, emptyValue) || isSelectedByDefault);
      }),
  );

  const { current: initialFilters } = useRef(computeDefaultFilters());
  const [filters$, setFilters] = useState$<TFilters>(initialFilters);

  const { current: initialMultiFilter } = useRef(computeInitialMultiFilter(initialFilters));
  const [multiFilter$, setMultiFilter] = useState$<Array<keyof TFilters>>(initialMultiFilter);

  const areFiltersClearable$ = useMapped$((ctx) => {
    const filters = ctx.spy(filters$);
    const multiFilter = ctx.spy(multiFilter$);
    const isSelectedByDefaultLength = filterKeys.filter((key) => filterConfigs[key].isSelectedByDefault).length;

    return (
      multiFilter.length > isSelectedByDefaultLength ||
      filterKeys.some((key) => {
        const { emptyValue, compare, disabledStoragePersist } = filterConfigs[key];
        if (disabledStoragePersist) return false;
        const currentValue = filters[key];
        return currentValue !== undefined && !compare(currentValue, emptyValue);
      })
    );
  });

  const persistFilter = useCallbackRef(<K extends keyof TFilters>(key: K, value: TFilters[K]) => {
    if (!hasPersistence) return;

    const { serialize, disabledStoragePersist } = filterConfigs[key];
    if (!serialize) return;

    const serializedValue = serialize(value);

    searchParamsRef.current[key] = serializedValue;

    setSearchParams(
      (prev) => {
        if (serializedValue) {
          prev.set(String(key), serializedValue);
        } else {
          prev.delete(String(key));
        }
        return prev;
      },
      { replace: true },
    );

    if (!disabledStoragePersist) {
      if (serializedValue) {
        saveStorageFilterField(String(key), serializedValue, filterConfigs[key].customScope);
      } else {
        removeStorageFilterField(String(key), filterConfigs[key].customScope);
      }
    }
  });

  const updateFilter = useCallbackRef<UpdateFilterFn<TFilters>>((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));

    const { isMainFilter, compare, emptyValue } = filterConfigs[key];

    // ensure that multi filter is visible if the filter is not empty
    if (!isMainFilter && !compare(value, emptyValue)) {
      setMultiFilter((prevMultiFilter) => {
        if (!prevMultiFilter.includes(key)) {
          return [...prevMultiFilter, key];
        }
        return prevMultiFilter;
      });
    }

    persistFilter(key, value);
  });

  const removeFilter = useCallbackRef((key: keyof TFilters) => {
    const { emptyValue } = filterConfigs[key];
    updateFilter(key, emptyValue);
    setMultiFilter((prevMultiFilter) => prevMultiFilter.filter((k) => k !== key));
  });

  const clearFilters = useCallbackRef(() => {
    setFilters((prev) => {
      const clearedFilters = { ...prev };

      for (const key of filterKeys) {
        const { emptyValue, disabledStoragePersist } = filterConfigs[key];
        if (disabledStoragePersist) continue;
        clearedFilters[key] = emptyValue;
      }

      return clearedFilters;
    });

    setMultiFilter(initialMultiFilter.filter((key) => filterConfigs[key].isSelectedByDefault));

    if (hasPersistence) {
      for (const key of filterKeys) {
        const { emptyValue, disabledStoragePersist } = filterConfigs[key];
        if (disabledStoragePersist) continue;
        persistFilter(key, emptyValue);
      }
    }
  });

  const isFilterHidden = useCallbackRef<IsFilterHiddenFn<TFilters>>(
    (filter, key) => Boolean(filterConfigs[filter].hidden) || (!!key && key !== filter),
  );

  useLayoutEffect(() => {
    if (!hasPersistence) {
      return;
    }
    for (const key of filterKeys) {
      const currentValue = initialFilters[key];

      // Skip async filters - they handle their own persistence after API calls
      if (!asyncFilterConfigsRef.current.find((config) => config.key === key)) {
        persistFilter(key, currentValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Process async filters: deserialize stored values via API calls
  useEffect(() => {
    if (!asyncFilterConfigsRef.current.length) return;

    const promises: Promise<unknown>[] = [];
    const persistedValues = getPersistedValues();

    asyncFilterConfigsRef.current.forEach(({ key, config, storedValue }) => {
      if (!hasPersistence) {
        return;
      }
      const { emptyValue, asyncDeserialize } = config;

      if (asyncDeserialize) {
        promises.push(
          asyncDeserialize(storedValue, persistedValues)
            .then((value) => {
              if (value !== null) {
                updateFilter(key, value);
              } else {
                updateFilter(key, emptyValue);
              }
            })
            .catch(() => {
              updateFilter(key, emptyValue);
            }),
        );
      }
    });

    setAreFiltersPreloading(true);
    Promise.all(promises).finally(() => {
      setAreFiltersPreloading(false);
    });

    // Clear processed configs to prevent re-processing
    asyncFilterConfigsRef.current = [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Removes filters that are hidden when the filter configs change
  useEffect(() => {
    const hiddenFilterKeys = filterKeys.filter((key) => filterConfigs[key].hidden);
    for (const key of hiddenFilterKeys) {
      if (!hiddenFilterKeysRef.current.includes(key)) {
        removeFilter(key);
      }
    }
    hiddenFilterKeysRef.current = hiddenFilterKeys;
  }, [filterConfigs, filterKeys, removeFilter]);

  // Updates filters from search params if they have changed externally
  useEffect(() => {
    const updateFromParams = async (params: Record<keyof TFilters, string | null>) => {
      const deserializers: Array<Promise<[keyof TFilters, FilterValue]>> = [];

      for (const key of filterKeys) {
        const config = filterConfigs[key];
        const paramValue = params[key];

        // store filter value to storage if it is not disabled
        if (!config.disabledStoragePersist) {
          if (paramValue) {
            saveStorageFilterField(String(key), paramValue, config.customScope);
          } else {
            removeStorageFilterField(String(key), config.customScope);
          }
        }

        // deserialize filter value
        if (paramValue !== null) {
          if (isSyncFilterConfig(config) && config.syncDeserialize) {
            deserializers.push(
              Promise.resolve(config.syncDeserialize(paramValue, getPersistedValues()))
                .catch(() => null)
                .then((value) => [key, value ?? config.emptyValue]),
            );
            continue;
          }
          if (isAsyncFilterConfig(config) && config.asyncDeserialize) {
            deserializers.push(
              config
                .asyncDeserialize(paramValue, getPersistedValues())
                .catch(() => null)
                .then((value) => [key, value ?? config.emptyValue]),
            );
            continue;
          }
        }
        deserializers.push(Promise.resolve([key, config.emptyValue]));
      }

      setAreFiltersPreloading(true);
      try {
        const results = await Promise.all(deserializers);
        const newFilters = Object.fromEntries(results) as TFilters;
        setFilters(newFilters);
        setMultiFilter(computeInitialMultiFilter(newFilters));
      } finally {
        setAreFiltersPreloading(false);
      }
    };

    if (hasPersistence) {
      const newSearchParams = queryParamsToObject(searchParams, filterKeys);
      if (!isEqual(searchParamsRef.current, newSearchParams)) {
        searchParamsRef.current = newSearchParams;
        updateFromParams(newSearchParams);
      }
    }
  }, [
    searchParams,
    filterKeys,
    hasPersistence,
    setFilters,
    filterConfigs,
    getPersistedValues,
    setMultiFilter,
    computeInitialMultiFilter,
    setAreFiltersPreloading,
    removeStorageFilterField,
    saveStorageFilterField,
  ]);

  const MultiFilter = useMemo(
    () => (
      <MultiFilterComponent
        multiFilter$={multiFilter$}
        filterKeys={filterKeys}
        filterConfigs={filterConfigs}
        setMultiFilter={setMultiFilter}
        removeFilter={removeFilter}
      />
    ),
    [multiFilter$, filterKeys, filterConfigs, setMultiFilter, removeFilter],
  );

  const ClearFiltersButton = useMemo(
    () => <ClearFiltersButtonComponent areFiltersClearable$={areFiltersClearable$} clearFilters={clearFilters} />,
    [areFiltersClearable$, clearFilters],
  );

  return {
    filters$,
    multiFilter$,
    updateFilter,
    areSearchParamFiltersEmpty,
    areStorageParamFiltersEmpty,
    areFiltersPreloading$,
    isFilterHidden,

    MultiFilter,
    ClearFiltersButton,
  };
};

export type { UpdateFilterFn, IsFilterHiddenFn } from './types.ts';
export {
  createStringFilterConfig,
  createNumberFilterConfig,
  createBooleanFilterConfig,
  createDateRangeFilterConfig,
  createSingleNullableEnumOptionFilterConfig,
  createSingleNonnullableEnumOptionFilterConfig,
  createMultiEnumOptionFilterConfig,
  createMultiOptionFilterConfigNonNullable,
  createMultiOptionFilterConfig,
  type ConfigOptions,
  type NonnullableConfigOptions,
} from './filterConfigs.ts';
